import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useLandingPlans } from "../../features/plans/hooks/useLandingPlans";
import { activateSampleEntry, HREF_AMOSTRA } from "./landingHeroCopy";
import { LandingPlanCard } from "./LandingPlanCard";
import {
  LandingPlansEmpty,
  LandingPlansError,
  LandingPlansSkeleton,
} from "./LandingPlansStates";
import { LANDING_PLAN_CLICK_ALL, track } from "./landingTrack";
import "./LandingPlans.css";

const PLANS_SUBTITLE =
  "Preço, período de acesso e regras de reembolso antes de você pagar.";

export default function LandingPlans() {
  const { status, plans, retry } = useLandingPlans();
  const showSectionCatalog = status === "loading" || status === "ready";
  // One record per outcome. The ref survives StrictMode's second effect run.
  // Loading clears it so a retry can record the next outcome. Cleanup does not.
  const recordedOutcome = useRef<string | null>(null);

  useEffect(() => {
    if (status === "loading") {
      recordedOutcome.current = null;
      return;
    }
    if (status === "ready") {
      const count = plans.length;
      const hasPopular = plans.some((plan) => plan.popular);
      const key = `${count}:${hasPopular}:${plans.map((plan) => plan.id).join(",")}`;
      if (recordedOutcome.current === key) return;
      recordedOutcome.current = key;
      track("landing_plans_viewed", { count, has_popular: hasPopular });
      return;
    }
    if (recordedOutcome.current === status) return;
    recordedOutcome.current = status;
    track("landing_plans_failed", { state: status });
  }, [status, plans]);

  return (
    <section
      id="planos"
      className="landing-plans"
      aria-labelledby="landing-plans-heading"
      aria-busy={status === "loading"}
    >
      <header className="landing-plans__header">
        <p className="landing-plans__eyebrow">PLANOS</p>
        <h2 id="landing-plans-heading" className="landing-plans__heading">
          Planos em destaque
        </h2>
        <p className="landing-plans__lede">{PLANS_SUBTITLE}</p>
      </header>
      {status === "loading" ? <LandingPlansSkeleton /> : null}
      {status === "ready" ? (
        <ul className="landing-plans__cards">
          {plans.map((plan) => (
            <li key={plan.id} className="landing-plans__item">
              <LandingPlanCard plan={plan} />
            </li>
          ))}
        </ul>
      ) : null}
      {status === "empty" ? <LandingPlansEmpty /> : null}
      {status === "error" || status === "timeout" ? (
        <LandingPlansError onRetry={retry} />
      ) : null}
      <div className="landing-plans__actions">
        {showSectionCatalog ? (
          <Link
            className="landing-plans__catalog"
            to="/planos"
            onClick={() => {
              track("landing_plan_clicked", { product_id: LANDING_PLAN_CLICK_ALL });
            }}
          >
            Ver planos
          </Link>
        ) : null}
        <a
          className="landing-plans__sample"
          href={HREF_AMOSTRA}
          onClick={activateSampleEntry}
        >
          ou responda a questão de exemplo
        </a>
      </div>
    </section>
  );
}
